// src\features\Cart\components\EditModal.tsx
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { formatCurrency } from "@/utils/formatCurrency";
import { Search, User } from "lucide-react";
import { useCustomersKasir } from "@/features/Customer/hooks/useCustomers";
import { getStoredUserData } from "@/utils/getStoredUser";

interface EditModalProps {
  isOpen: boolean;
  field: "customer" | "discount" | "ppn" | null;
  currentValue: string;
  subtotal?: number;
  onClose: () => void;
  onSave: (
    field: "customer" | "discount" | "ppn",
    value: string,
    unused?: string,
    customerId?: string,
  ) => void;
}


export default function EditModal({
  isOpen,
  field,
  currentValue,
  subtotal = 0,
  onClose,
  onSave,
}: EditModalProps) {
  const [value, setValue] = useState(currentValue);
  // inputType hanya untuk UI input di modal, tidak dikirim keluar
  const [inputType, setInputType] = useState<"percent" | "amount">("amount");

  const userData = getStoredUserData();
  const email = userData?.email || "";

  // State untuk customer search
  const [searchName, setSearchName] = useState("");
  const [debouncedName, setDebouncedName] = useState("");

  // Hook untuk fetch customers
  const { data: customers, isLoading } = useCustomersKasir(
    email,
    debouncedName,
  );

  useEffect(() => {
    setValue(currentValue);
    // Reset ke amount ketika modal dibuka dengan value rupiah
    setInputType("amount");
  }, [currentValue]);

  // Debounce effect untuk search email
  useEffect(() => {
    if (searchName.length >= 3) {
      const timer = setTimeout(() => {
        setDebouncedName(searchName);
      }, 500); // 500ms debounce

      return () => clearTimeout(timer);
    } else {
      setDebouncedName("");
    }
  }, [searchName]);

  // Reset search saat modal ditutup
  useEffect(() => {
    if (!isOpen) {
      setSearchName("");
      setDebouncedName("");
    }
  }, [isOpen]);

  if (!isOpen || !field) return null;

  const fieldLabels = {
    customer: "Pilih Customer",
    discount: "Diskon Subtotal",
    ppn: "PPN",
  };

  // Konversi input ke rupiah sebelum dikirim
  const convertToRupiah = (): number => {
    const numValue = Number(value) || 0;
    if (inputType === "percent") {
      return (subtotal * numValue) / 100;
    }
    return numValue;
  };

  const handleSave = () => {
    if (field === "discount" || field === "ppn") {
      const rupiahValue = convertToRupiah();
      onSave(field, String(rupiahValue));
    } else {
      onSave(field, value);
    }
  };

  const handleSelectCustomer = (customerName: string, customerId: string) => {
    setValue(customerName);
    onSave("customer", customerId, undefined, customerName);
    setSearchName("");
    setDebouncedName("");
  };

  const numValue = Number(value) || 0;
  let calculatedAmount = 0;
  let finalTotal = subtotal;

  if ((field === "discount" || field === "ppn") && numValue > 0) {
    if (inputType === "percent") {
      calculatedAmount = subtotal * (numValue / 100);
    } else {
      calculatedAmount = numValue;
    }

    if (field === "discount") {
      finalTotal = subtotal - calculatedAmount;
    } else {
      finalTotal = subtotal + calculatedAmount;
    }
  }

  if (finalTotal < 0) finalTotal = 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-[#37393d] rounded-2xl p-6 w-[90%] max-w-md shadow-2xl z-10 animate-in fade-in zoom-in duration-200">
        <h2 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
          {fieldLabels[field]}
        </h2>

        {(field === "discount" || field === "ppn") && (
          <>
            <div className="bg-gray-50 dark:bg-[#1e1e1e] rounded-lg p-4 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-white">Subtotal</span>
                <span className="font-semibold dark:text-white">
                  {formatCurrency(String(subtotal))}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                Tipe {field === "ppn" ? "PPN" : "Diskon"}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    setInputType("percent");
                    setValue("");
                  }}
                  className={`px-4 py-2.5 rounded-lg font-medium transition ${
                    inputType === "percent"
                      ? "bg-[#d7d0fe] text-[#37393d]"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  Persen (%)
                </button>
                <button
                  onClick={() => {
                    setInputType("amount");
                    setValue("");
                  }}
                  className={`px-4 py-2.5 rounded-lg font-medium transition ${
                    inputType === "amount"
                      ? "bg-[#d7d0fe] text-[#37393d]"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  Rupiah (Rp)
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                Nilai {field === "ppn" ? "PPN" : "Diskon"}{" "}
                {inputType === "percent" ? "(%)" : "(Rp)"}
              </label>
              <div className="relative">
                <span className="absolute font-medium text-gray-500 -translate-y-1/2 left-3 top-1/2 dark:text-white">
                  {inputType === "percent" ? "%" : "Rp"}
                </span>
                <Input
                  type="number"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={
                    inputType === "percent" ? "Misal: 10" : "Misal: 5000"
                  }
                  className="pl-10 dark:text-white"
                  autoFocus
                />
              </div>
            </div>

            {numValue > 0 && (
              <div
                className={` ${
                  field === "ppn"
                    ? "bg-blue-50 border-blue-200"
                    : "bg-green-50 border-green-200"
                } border rounded-lg p-4 mb-4`}
              >
                <div className="flex justify-between mb-2 text-sm">
                  <span
                    className={`${
                      field === "ppn" ? "text-blue-700" : "text-green-700"
                    } font-medium`}
                  >
                    {field === "ppn" ? "Tambahan PPN" : "Potongan"}
                  </span>
                  <span
                    className={`${
                      field === "ppn" ? "text-blue-700" : "text-green-700"
                    } font-semibold`}
                  >
                    {field === "ppn" ? "+" : "-"}
                    {formatCurrency(String(calculatedAmount))}
                  </span>
                </div>
                <div
                  className={`flex justify-between text-base border-t ${
                    field === "ppn" ? "border-blue-200" : "border-green-200"
                  } pt-2`}
                >
                  <span
                    className={`${
                      field === "ppn" ? "text-blue-800" : "text-green-800"
                    } font-semibold`}
                  >
                    Total Akhir
                  </span>
                  <span
                    className={`${
                      field === "ppn" ? "text-blue-800" : "text-green-800"
                    } font-bold`}
                  >
                    {formatCurrency(String(finalTotal))}
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {field === "customer" && (
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
              Cari Nama Customer
            </label>
            <div className="relative mb-4">
              <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
              <Input
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="Ketik minimal 3 huruf ..."
                className="pl-10 dark:text-white"
                autoFocus
              />
            </div>

            {/* Loading state */}
            {isLoading && debouncedName && (
              <div className="py-4 text-sm text-center text-gray-500 dark:text-gray-400">
                Mencari customer...
              </div>
            )}

            {/* Customer list */}
            {!isLoading && customers && customers.length > 0 && (
              <div className="mb-4 space-y-2 overflow-y-auto max-h-60">
                {customers.map((customer) => (
                  <button
                    key={customer.cust_id}
                    onClick={() =>
                      handleSelectCustomer(customer.cust_name, customer.cust_id)
                    }
                    className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-[#7c3aed] hover:bg-purple-50 dark:border-gray-600 dark:hover:border-[#7c3aed] dark:hover:bg-[#1e1e1e] transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#d7d0fe] flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-[#7c3aed]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-white group-hover:text-[#7c3aed]">
                          {customer.cust_name}
                        </div>
                        <div className="text-sm text-gray-500 truncate dark:text-gray-400">
                          {customer.cust_email}
                        </div>
                        <div className="text-xs text-gray-400 dark:text-gray-500">
                          {customer.cust_phone}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* No results */}
            {!isLoading &&
              debouncedName &&
              customers &&
              customers.length === 0 && (
                <div className="py-8 text-sm text-center text-gray-500 dark:text-gray-400">
                  Tidak ada customer ditemukan
                </div>
              )}

            {/* Info text */}
            {!debouncedName &&
              searchName.length > 0 &&
              searchName.length < 3 && (
                <div className="py-4 text-xs text-center text-gray-400 dark:text-gray-500">
                  Ketik minimal 3 huruf untuk mencari
                </div>
              )}
          </div>
        )}

        <div className="flex gap-3">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Batal
          </Button>
          {field !== "customer" && (
            <Button variant="default" className="flex-1" onClick={handleSave}>
              Simpan
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
